import { useRef, useState } from "react";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { SCREEN_MANIFEST } from "@/generated/screenManifest.mjs";

const CAPTION =
  "Design preview with demo data. The app may look different at launch.";

// Cards whose image file is listed in the build-time screen manifest render
// only when the file exists in public/ (removed off-spec screens return by
// dropping a replacement at the same path — no code change). posCompact is
// used when any card is absent, so the remaining cards still frame the phone
// (diagonal balance, no empty side).
const CARDS = [
  {
    key: "family",
    src: "/hero/hero-card-family.jpg",
    alt: "NISCHINT Family Circle card — guardian online",
    pos: "top-[0%] left-0 w-[35%]",
    posCompact: "top-[0%] left-0 w-[35%]",
    rot: "-3deg",
    depth: 1.0,
    w: 2000,
    h: 600,
    label: (
      <>
        Guardians see status <span className="text-[#14171A] font-bold">live</span>
      </>
    ),
  },
  {
    key: "sos",
    src: "/hero/hero-card-sos.jpg",
    flag: "/hero/hero-card-sos.jpg",
    alt: "NISCHINT SOS triggered alert card",
    pos: "top-[9%] right-0 w-[35%]",
    rot: "3deg",
    depth: 0.75,
    w: 1980,
    h: 1640,
    label: (
      <>
        Hold SOS for 3 seconds to <span className="text-[#14171A] font-bold">alert your circle</span>
      </>
    ),
  },
  {
    key: "zone",
    src: "/hero/hero-card-zone.jpg",
    alt: "NISCHINT Office safe-zone card",
    pos: "bottom-[3%] left-0 w-[37%]",
    posCompact: "bottom-[3%] right-0 w-[37%]",
    rot: "2deg",
    depth: 1.15,
    w: 1960,
    h: 740,
    label: (
      <>
        Zones <span className="text-[#14171A] font-bold">alert</span>, not just mark
      </>
    ),
  },
  {
    key: "monitoring",
    src: "/hero/hero-card-monitoring.jpg",
    flag: "/hero/hero-card-monitoring.jpg",
    alt: "NISCHINT monitoring systems status grid",
    pos: "bottom-[1%] right-0 w-[37%]",
    rot: "-2deg",
    depth: 0.9,
    w: 2000,
    h: 940,
    label: (
      <>
        Every system, <span className="text-[#14171A] font-bold">always on</span>
      </>
    ),
  },
];

const VISIBLE_CARDS = CARDS.filter((c) => !c.flag || SCREEN_MANIFEST[c.flag]);
const COMPACT = VISIBLE_CARDS.length !== CARDS.length;

// Image with a shimmer placeholder until loaded. Width/height attrs reserve
// layout space pre-load (no CLS); shimmer sits under the image, never over text.
const FadeImg = ({ src, alt, width, height, eager, className }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <span className={`relative block overflow-hidden ${className || ""}`}>
      {!loaded && <span aria-hidden="true" className="hero-img-skeleton absolute inset-0" />}
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={eager ? "eager" : "lazy"}
        fetchpriority={eager ? "high" : "auto"}
        onLoad={() => setLoaded(true)}
        className="w-full h-auto block transition-opacity duration-300"
        style={{ opacity: loaded ? 1 : 0 }}
      />
    </span>
  );
};

// Scroll-parallax layer: children drift by `amount` px across the section's
// scroll pass. Cards get a larger amount than the phone (fore/background split).
const ScrollLayer = ({ progress, amount, children }) => {
  const y = useTransform(progress, [0, 1], [amount, -amount]);
  return <motion.div style={{ y }}>{children}</motion.div>;
};

// Mouse-tilt layer: very subtle cursor-following translate (max ~7px).
const TiltLayer = ({ mvX, mvY, depth, children }) => {
  const x = useTransform(mvX, (v) => v * depth * 6);
  const y = useTransform(mvY, (v) => v * depth * 6);
  return <motion.div style={{ x, y }}>{children}</motion.div>;
};

export const HeroComposite = () => {
  const reduceMotion = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const mvX = useMotionValue(0);
  const mvY = useMotionValue(0);
  const tiltX = useSpring(mvX, { stiffness: 120, damping: 18 });
  const tiltY = useSpring(mvY, { stiffness: 120, damping: 18 });

  const onMouseMove = (e) => {
    if (reduceMotion || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mvX.set(((e.clientX - r.left) / r.width - 0.5) * 2);
    mvY.set(((e.clientY - r.top) / r.height - 0.5) * 2);
  };
  const onMouseLeave = () => {
    mvX.set(0);
    mvY.set(0);
  };

  // Entrance reveal: phone first (after the hero text), cards ~100ms apart.
  const enter = (delay) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 16 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.5, delay, ease: "easeOut" },
        };

  // Parallax + tilt are skipped entirely under prefers-reduced-motion.
  const Layers = ({ amount, depth, children }) =>
    reduceMotion ? (
      children
    ) : (
      <ScrollLayer progress={scrollYProgress} amount={amount}>
        <TiltLayer mvX={tiltX} mvY={tiltY} depth={depth}>
          {children}
        </TiltLayer>
      </ScrollLayer>
    );

  return (
    <div
      data-testid="hero-composite"
      ref={ref}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      className="w-full max-w-[500px] mx-auto"
    >
      <div className="relative w-full aspect-[10/11]">
        <div
          data-testid="hero-phone"
          className="absolute left-1/2 top-1/2 w-[45%] z-10"
          style={{ transform: "translate(-50%, -50%) rotate(-4deg)" }}
        >
          <Layers amount={10} depth={0.5}>
            {/* LCP element: NO opacity-0 start — Chrome won't count a hidden
                paint as LCP, so fading this in delayed mobile LCP by seconds.
                Slide-only entrance keeps the motion without hiding the paint. */}
            <motion.div
              initial={reduceMotion ? false : { y: 16 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <div className="rounded-[2.2rem] border-[7px] border-[#14171A] bg-[#14171A] shadow-[0_20px_60px_rgba(20,23,26,0.25)] overflow-hidden">
                <FadeImg
                  src="/hero/hero-phone-alert.jpg"
                  alt="NISCHINT app — Alert Center screen"
                  width={520}
                  height={1128}
                  eager
                />
              </div>
            </motion.div>
          </Layers>
        </div>
        {VISIBLE_CARDS.map((card, i) => (
          <div
            key={card.key}
            data-testid={`float-card-${card.key}`}
            className={`absolute ${COMPACT && card.posCompact ? card.posCompact : card.pos} z-20`}
            style={{ transform: `rotate(${card.rot})` }}
          >
            <Layers amount={16 * card.depth} depth={card.depth}>
              <motion.div {...enter(0.65 + i * 0.1)}>
                <motion.div
                  animate={reduceMotion ? {} : { y: [0, -8, 0] }}
                  transition={
                    reduceMotion
                      ? {}
                      : { duration: 5, repeat: Infinity, ease: "easeInOut", delay: i * 0.8 }
                  }
                  whileHover={
                    reduceMotion
                      ? undefined
                      : { y: -4, transition: { duration: 0.2, ease: "easeOut" } }
                  }
                  whileTap={
                    reduceMotion ? undefined : { y: -2, transition: { duration: 0.15 } }
                  }
                  className="bg-white rounded-xl border border-[#E3DBC9] shadow-[0_10px_30px_rgba(20,23,26,0.12)] hover:shadow-[0_18px_44px_rgba(20,23,26,0.2)] transition-shadow duration-200 p-2"
                >
                  <FadeImg
                    src={card.src}
                    alt={card.alt}
                    width={card.w}
                    height={card.h}
                    className="rounded-lg"
                  />
                  <p className="mt-1.5 mb-0.5 text-[11px] leading-snug font-semibold text-[#726B5E] text-center">
                    {card.label}
                  </p>
                </motion.div>
              </motion.div>
            </Layers>
          </div>
        ))}
      </div>
      <p
        data-testid="hero-composite-caption"
        className="mt-5 text-xs text-[#726B5E] text-center"
      >
        {CAPTION}
      </p>
    </div>
  );
};
